import {
	Avatar,
	Badge,
	BadgeProps,
	Card,
	CardActions,
	CardHeader,
	IconButton,
	keyframes,
	Link,
	Stack,
	styled,
	Typography,
} from "@mui/material";
import {
	DeviceState,
	DeviceStatus,
	DeviceCategory,
} from "Infrastructure/Models/Messages/Dto/DeviceStatus";
import DnsIcon from "@mui/icons-material/Dns";
import AnnouncementIcon from "@mui/icons-material/Announcement";
import CachedIcon from "@mui/icons-material/Cached";
import CloudSyncIcon from "@mui/icons-material/CloudSync";
import SettingsIcon from "@mui/icons-material/Settings";
import CategoryIcon from "@mui/icons-material/Category";
import RuleIcon from "@mui/icons-material/Rule";
import { StatusBadge } from "Shared/Badges/StatusBadge";
import InstanceActionMenu from "./InstanceActionMenu";
import { BlTooltip } from "Shared/BlTooltip";
import { useDialog } from "Infrastructure/Providers/DialogProvider/DialogProvider";
import InfoWarningsDialogContent from "./InfoWarnings/InfoWarningsDialogContent";
import { formatDate } from "Infrastructure/Utils/DateUtils";
import { HubConnection, HubConnectionState } from "@microsoft/signalr";
import { logError } from "Infrastructure/Utils/LoggingUtils";
import { useSnackbar } from "Infrastructure/Providers/SnackbarProvider/SnackbarProvider";
import {
	CacheRefreshMessage,
	DeviceStatusRequestMessage,
} from "Infrastructure/Models/Messages/Messages";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import InstanceResponseContent from "./InstanceResponseContent";
import { useEffect, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import SystemSettingsDialogContent from "./SystemSettings/SystemSettingsDialogContent";
import FeaturesDialogContent from "./Features/FeaturesDialogContent";
import ProductsDialogContent from "./Products/ProductsDialogContent";
import { InfoWarningState } from "Infrastructure/Models/Messages/Dto/InfoWarning";
import { useUserInfo } from "Shared/Contexts/UserInfoContext";
import React from "react";
import EngineeringIcon from "@mui/icons-material/Engineering";
import CoPresentIcon from "@mui/icons-material/CoPresent";
import GroupIcon from "@mui/icons-material/Group";
import PersonIcon from "@mui/icons-material/Person";
import { getDeviceStateColor } from "Infrastructure/Utils/Nsure/DeviceUtils";

type Props = {
	deviceStatuses: DeviceStatus[] | null;
	deviceStatus: DeviceStatus;
	hubConnection: HubConnection | null;
};

const StyledBadge = styled(Badge)<BadgeProps>(({ theme }) => ({
	"& .MuiBadge-badge": {
		border: `2px solid ${theme.palette.background.paper}`,
		padding: "0 3px",
		fontSize: 9,
	},
}));

const InstanceCard: React.FunctionComponent<Props> = (props) => {
	const { deviceStatuses, deviceStatus, hubConnection } = props;
	const { showDialog, closeDialog } = useDialog();
	const showSnackbar = useSnackbar();
	const { t } = useResource();

	const [messageResponse, setMessageResponse] = useState<{
		actionName: string;
		requestId?: string | null;
		deviceNames: string[];
	} | null>(null);

	const userInfo = useUserInfo();
	const isActionAccessible = (accessRight: string) =>
		userInfo.accessRightCodes.some((x) => x === accessRight);

	const [isHighlighted, setIsHighlighted] = useState(false);
	const prevDateUpdatedRef = useRef(deviceStatus.dateUpdated);
	useEffect(() => {
		if (deviceStatus.dateUpdated !== prevDateUpdatedRef.current) {
			setIsHighlighted(true);
			setTimeout(() => setIsHighlighted(false), 1000);
			prevDateUpdatedRef.current = deviceStatus.dateUpdated;
		}
	}, [deviceStatus.dateUpdated]);

	const handleShowInfoWarningDialog = async () => {
		await showDialog({
			title: `[${deviceStatus.deviceName}] Infolišta`,
			content: (
				<InfoWarningsDialogContent
					deviceStatuses={deviceStatuses}
					deviceStatus={deviceStatus}
					hubConnection={hubConnection}
					closeDialog={closeDialog}
				/>
			),
		});
	};

	const handleShowSystemSettingDialog = async () => {
		await showDialog({
			title: `[${deviceStatus.deviceName}] System settings`,
			content: (
				<SystemSettingsDialogContent
					deviceStatuses={deviceStatuses}
					deviceStatus={deviceStatus}
					hubConnection={hubConnection}
					closeDialog={closeDialog}
				/>
			),
		});
	};

	const handleShowFeatureDialog = async () => {
		await showDialog({
			title: `[${deviceStatus.deviceName}] Features`,
			content: (
				<FeaturesDialogContent
					deviceStatuses={deviceStatuses}
					deviceStatus={deviceStatus}
					hubConnection={hubConnection}
					closeDialog={closeDialog}
				/>
			),
		});
	};

	const handleShowProductDialog = async () => {
		await showDialog({
			title: `[${deviceStatus.deviceName}] Produkty`,
			content: (
				<ProductsDialogContent
					deviceStatuses={deviceStatuses}
					deviceStatus={deviceStatus}
					hubConnection={hubConnection}
					closeDialog={closeDialog}
				/>
			),
		});
	};

	const handleRefreshCache = async () => {
		if (
			!hubConnection ||
			hubConnection.state !== HubConnectionState.Connected
		) {
			showSnackbar(t(Resources.Errors.Hub.ConnectionError), "error");
			return;
		}

		const requestId = uuidv4();
		const cacheRefreshMessage: CacheRefreshMessage = {
			requestId: requestId,
			filteredDeviceNames: [deviceStatus.deviceName],
		};

		hubConnection
			.invoke("cacheRefresh", cacheRefreshMessage)
			.catch((error: Error) => {
				showSnackbar(t(Resources.Errors.Hub.RequestError), "error");
				logError(error);
			});
		await showDialog({
			title: `[${deviceStatus.deviceName}] Refresh cache`,
			content: (
				<InstanceResponseContent
					actionName="Potvrzení akce"
					requestId={cacheRefreshMessage.requestId}
					deviceNames={cacheRefreshMessage.filteredDeviceNames}
					hubConnection={hubConnection}
					closeDialog={closeDialog}
				/>
			),
		});
	};

	const handleDeviceStatusSync = () => {
		if (
			!hubConnection ||
			hubConnection.state !== HubConnectionState.Connected
		) {
			showSnackbar(t(Resources.Errors.Hub.ConnectionError), "error");
			return;
		}
		const deviceStatusMessage: DeviceStatusRequestMessage = {
			filteredDeviceNames: [deviceStatus.deviceName],
		};
		hubConnection
			.invoke("deviceStatusSync", deviceStatusMessage)
			.catch((error: Error) => {
				showSnackbar(t(Resources.Errors.Hub.RequestError), "error");
				logError(error);
			});
	};

	const statusBadgeColor = getDeviceStateColor(deviceStatus.deviceState);
	const isDisabled = deviceStatus.deviceState !== DeviceState.Connected;

	const highlightAnimation = keyframes`
		0% { background-color: #fff27d; }
		100% { background-color: #ffffff; }
`;

	const getAuthUrl = (baseUrl?: string) => {
		if (!baseUrl) return "";
		return baseUrl.endsWith("/")
			? `${baseUrl}auth/login`
			: `${baseUrl}/auth/login`;
	};

	const getAvatarConfig = () => {
		switch (deviceStatus.deviceCategory) {
			case DeviceCategory.Internal:
				return {
					icon: <EngineeringIcon />,
				};
			case DeviceCategory.Demo:
				return {
					icon: <CoPresentIcon />,
				};
			default:
				return {
					icon: deviceStatus.isSelfInsurance ? <PersonIcon /> : <GroupIcon />,
					backgroundColor: deviceStatus.isSelfInsurance ? "#4b83dd" : "#034796",
					color: "#ffffff",
				};
		}
	};

	return (
		<Card
			sx={{
				maxWidth: 345,
				backgroundColor: isDisabled ? "#f7f7f7" : "#ffffff",
				animation: isHighlighted
					? `${highlightAnimation} 1s ease-in-out`
					: "none",
			}}
		>
			<CardHeader
				sx={{ px: 1, py: 0.7 }}
				avatar={
					<Link
						href={getAuthUrl(deviceStatus.appUrls?.[0])}
						target="_blank"
						rel="noopener noreferrer"
					>
						<StatusBadge
							statusColor={statusBadgeColor}
							anchorVertical="bottom"
							anchorHorizontal="right"
							disableAnimation={isDisabled}
						>
							<Avatar
								sx={{
									bgcolor: getAvatarConfig().backgroundColor,
									color: getAvatarConfig().color,
								}}
							>
								{getAvatarConfig().icon}
							</Avatar>
						</StatusBadge>
					</Link>
				}
				action={
					<InstanceActionMenu
						open={true}
						isDisabled={isDisabled}
						deviceStatuses={deviceStatuses}
						deviceStatus={deviceStatus}
						hubConnection={hubConnection}
					/>
				}
				title={
					<Link
						href={getAuthUrl(deviceStatus.appUrls?.[0])}
						underline="hover"
						target="_blank"
						rel="noopener noreferrer"
					>
						<BlTooltip title={deviceStatus.deviceName} enterDelay={1000}>
							<Typography noWrap maxWidth="150px">
								{deviceStatus.deviceName}
							</Typography>
						</BlTooltip>
					</Link>
				}
				subheader={formatDate(new Date(deviceStatus.dateUpdated))}
			/>

			<CardActions disableSpacing sx={{ pt: 0 }}>
				<Stack direction="row">
					{!deviceStatus.isSelfInsurance && (
						<>
							<BlTooltip title="Infolišta">
								<IconButton
									onClick={handleShowInfoWarningDialog}
									disabled={
										isDisabled ||
										!isActionAccessible("InstanceManagementInfoWarnings")
									}
								>
									<StyledBadge
										badgeContent={
											!isDisabled
												? deviceStatus.infoWarnings.filter(
														(x) => x.state === InfoWarningState.Active
												  ).length
												: 0
										}
										anchorOrigin={{ vertical: "top", horizontal: "right" }}
										color="primary"
									>
										<AnnouncementIcon />
									</StyledBadge>
								</IconButton>
							</BlTooltip>

							<BlTooltip title="Features">
								<IconButton
									onClick={handleShowFeatureDialog}
									disabled={
										isDisabled ||
										!isActionAccessible("InstanceManagementFeatures")
									}
								>
									<StyledBadge
										badgeContent={
											!isDisabled
												? deviceStatus.features.filter((x) => x.isEnabled)
														.length
												: 0
										}
										max={999}
										anchorOrigin={{ vertical: "top", horizontal: "right" }}
										color="primary"
									>
										<RuleIcon />
									</StyledBadge>
								</IconButton>
							</BlTooltip>

							<BlTooltip title="Produkty">
								<IconButton
									onClick={handleShowProductDialog}
									disabled={
										isDisabled ||
										!isActionAccessible("InstanceManagementProducts")
									}
								>
									<StyledBadge
										badgeContent={
											!isDisabled
												? deviceStatus.productStatus?.categories.filter(
														(x) => x.isEnabled
												  ).length
												: 0
										}
										max={999}
										anchorOrigin={{ vertical: "top", horizontal: "right" }}
										color="primary"
									>
										<CategoryIcon />
									</StyledBadge>
								</IconButton>
							</BlTooltip>

							<BlTooltip title="System settings">
								<IconButton
									onClick={handleShowSystemSettingDialog}
									disabled={
										isDisabled ||
										!isActionAccessible("InstanceManagementSystemSettings")
									}
								>
									<SettingsIcon />
								</IconButton>
							</BlTooltip>
						</>
					)}
					<BlTooltip title="Refresh cache">
						<IconButton
							onClick={handleRefreshCache}
							disabled={
								isDisabled ||
								!isActionAccessible("InstanceManagementRefreshCache")
							}
						>
							<CachedIcon />
						</IconButton>
					</BlTooltip>
				</Stack>
				<Stack direction="row" sx={{ marginLeft: "auto" }}>
					<BlTooltip title="Synchronizace">
						<IconButton
							onClick={handleDeviceStatusSync}
							disabled={!isActionAccessible("InstanceManagementSyncDevice")}
						>
							<CloudSyncIcon />
						</IconButton>
					</BlTooltip>
				</Stack>
			</CardActions>
		</Card>
	);
};

export default React.memo(InstanceCard);
